import type { FC, ReactNode } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment-timezone";
import { useApolloClient } from "@apollo/client";
import { CrudLink, CustomCellDate } from "@/shared/components";
import { PagePaths } from "@/admin/shared/constants";
import { RefundButton } from "@/admin/features/refunds";
import type { PaymentShowData } from "@/admin/entities/payments";
import { renderPaymentStatus } from "./paymentStatus";
import { PaymentAmounts } from "./PaymentAmounts";
import { PaymentJsonPanels } from "./PaymentJsonPanels";
import { PaymentRefunds } from "./PaymentRefunds";
import { PaymentDisputes } from "./PaymentDisputes";

interface PaymentDetailsProps {
  /** CrudShow разворачивает данные showQuery в пропсы компонента. */
  payment?: PaymentShowData | null;
  /** id платежа CrudShow передаёт отдельным пропом; здесь не нужен. */
  id?: string;
}

// Тот же локальный маппинг типа продукта, что и в feature productPurchases —
// admin не переиспользует partner-локализацию, здесь нет i18n.
const typeLabel: Record<string, string> = {
  PPV: "Разовая покупка",
  SUBSCRIPTION: "Подписка",
};

const formatDate = (date: string) => moment(date).format("DD.MM.YYYY HH:mm");

const InfoItem: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" component="div">
      {children}
    </Typography>
  </Box>
);

const MonospaceValue: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography variant="body1" component="span" sx={{ fontFamily: "monospace" }}>
    {children}
  </Typography>
);

export const PaymentDetails: FC<PaymentDetailsProps> = ({ payment }) => {
  const client = useApolloClient();

  // Возврат меняет и сам платёж (статус REFUNDED после полного возврата),
  // и список возвратов, поэтому перезапрашиваем обе активные операции.
  const handleRefundCompleted = () => {
    void client.refetchQueries({ include: ["PaymentShow", "RefundsList"] });
  };

  if (!payment) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          Платёж не найден
        </Typography>
      </Paper>
    );
  }

  const { buyer, purchase, platform } = payment;
  const buyerTitle = buyer?.displayName || buyer?.email || payment.buyerEmail;
  const geo = [payment.country, payment.city].filter(Boolean).join(", ");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h5" component="h2">
                {payment.amount.value} {payment.amount.currency}
              </Typography>
              {renderPaymentStatus(payment.status)}
            </Stack>
            {payment.status === "SUCCEEDED" && (
              <RefundButton
                paymentId={payment.id}
                provider={payment.provider}
                onCompleted={handleRefundCompleted}
              />
            )}
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <InfoItem label="Провайдер">{payment.provider}</InfoItem>
            <InfoItem label="Покупатель">
              {buyer ? (
                <CrudLink path={PagePaths.users} tab="details" id={buyer.id}>
                  {buyerTitle ?? buyer.id}
                </CrudLink>
              ) : (
                (buyerTitle ?? "—")
              )}
            </InfoItem>
            <InfoItem label="ID платежа у провайдера">
              <MonospaceValue>
                {payment.providerPaymentId ?? "—"}
              </MonospaceValue>
            </InfoItem>
            <InfoItem label="ID транзакции у провайдера">
              <MonospaceValue>
                {payment.providerTransactionId ?? "—"}
              </MonospaceValue>
            </InfoItem>
            <InfoItem label="Продукт">{payment.product?.title ?? "—"}</InfoItem>
            <InfoItem label="Тип продукта">
              {payment.product
                ? (typeLabel[payment.product.type] ?? payment.product.type)
                : "—"}
            </InfoItem>
            <InfoItem label="Покупка">
              {purchase ? (
                <Stack spacing={0.5}>
                  <CrudLink
                    path={PagePaths.productPurchases}
                    tab="details"
                    id={purchase.id}
                  >
                    {typeLabel[purchase.type] ?? purchase.type}
                  </CrudLink>
                  <Typography variant="caption" color="text.secondary">
                    Доступ с {formatDate(purchase.startAt as string)} по{" "}
                    {formatDate(purchase.expiredAt as string)}
                  </Typography>
                </Stack>
              ) : (
                "—"
              )}
            </InfoItem>
            <InfoItem label="Период доступа, дней">{payment.period}</InfoItem>
            <InfoItem label="Организация">
              <CrudLink
                path={PagePaths.organizations}
                tab="details"
                id={payment.organizationId}
              >
                {payment.organization?.title ?? "—"}
              </CrudLink>
            </InfoItem>
            <InfoItem label="Платформа">
              <CrudLink
                path={PagePaths.platforms}
                tab="details"
                id={platform?.id ?? payment.platformId}
              >
                {platform?.title ?? payment.platformId}
              </CrudLink>
            </InfoItem>
            <InfoItem label="Промокод">
              <MonospaceValue>{payment.promocodeId ?? "—"}</MonospaceValue>
            </InfoItem>
            <InfoItem label="Гео покупателя">{geo || "—"}</InfoItem>
            <InfoItem label="Автопродление">
              {payment.recurrent ? "Да" : "Нет"}
            </InfoItem>
            <InfoItem label="ID платежа">
              <MonospaceValue>{payment.id}</MonospaceValue>
            </InfoItem>
            <InfoItem label="Дата создания">
              <CustomCellDate date={payment.createdAt as string} />
            </InfoItem>
            <InfoItem label="Дата обновления">
              <CustomCellDate date={payment.updatedAt as string} />
            </InfoItem>
          </Box>
        </CardContent>
      </Card>

      <PaymentAmounts payment={payment} />
      <PaymentRefunds paymentId={payment.id} />
      <PaymentDisputes paymentId={payment.id} />
      <PaymentJsonPanels payment={payment} />
    </Box>
  );
};
